import { zodResolver } from '@hookform/resolvers/zod';
import { importUrlRequestSchema, type ImportUrlRequest } from '@recipes/contracts';
import { Alert, Button, Checkbox, Input } from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { ApiClientError } from '@recipes/api-client';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';
import { FormField } from '@/features/shared/form-field';
import { HouseholdSelect } from '@/features/shared/household-select';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';

type FormValues = z.input<typeof importUrlRequestSchema>;

export function ImportUrlPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();

  const form = useForm<FormValues, unknown, ImportUrlRequest>({
    resolver: zodResolver(importUrlRequestSchema),
    defaultValues: { url: params.get('url') ?? '', allowAiFallback: false, householdId: null },
  });

  const mutation = useMutation({
    mutationFn: (body: ImportUrlRequest) => api.imports.fromUrl(body, crypto.randomUUID()),
    onSuccess: (job) => navigate(`/add/import/${job.id}`),
  });

  const error = mutation.error;
  const importErrorCode =
    error instanceof ApiClientError &&
    (error.code === 'IMPORT_BLOCKED' || error.code === 'IMPORT_FAILED')
      ? (error.error.details as { errorCode?: string } | undefined)?.errorCode
      : undefined;

  return (
    <PageContainer className="max-w-2xl">
      <FeaturePageHeader
        title={t('imports.url.title')}
        description={t('imports.url.description')}
        backTo="/add"
      />
      <form
        noValidate
        className="space-y-5"
        onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
      >
        <FormField
          label={t('imports.url.field')}
          required
          error={form.formState.errors.url?.message && t('imports.url.invalid')}
        >
          <Input
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://…"
            data-testid="import-url-input"
            {...form.register('url')}
          />
        </FormField>

        <HouseholdSelect
          value={form.watch('householdId') ?? null}
          onChange={(v) => form.setValue('householdId', v)}
        />

        {aiEnabled && (
          <Controller
            control={form.control}
            name="allowAiFallback"
            render={({ field }) => (
              <div className="space-y-1">
                <label className="flex min-h-[44px] items-start gap-3">
                  <Checkbox
                    className="mt-0.5"
                    checked={Boolean(field.value)}
                    disabled={!consented}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  <span>
                    <span className="font-medium">{t('imports.url.aiFallback')}</span>
                    <br />
                    <span className="text-sm text-muted-foreground">
                      {t('imports.url.aiFallbackHint')}
                    </span>
                  </span>
                </label>
                {!consented && (
                  <AiConsentNotice
                    variant="inline"
                    className="pl-8 text-sm text-muted-foreground"
                  />
                )}
              </div>
            )}
          />
        )}

        {error && (
          <Alert variant="destructive" title={t('imports.url.failed')} data-testid="import-error">
            <p>
              {importErrorCode
                ? t(`imports.errors.${importErrorCode}`, { defaultValue: describeError(error) })
                : describeError(error)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{t('imports.errors.noBypass')}</p>
          </Alert>
        )}

        <p className="text-xs text-muted-foreground">{t('imports.url.compliance')}</p>

        <Button type="submit" size="lg" loading={mutation.isPending} data-testid="import-submit">
          {t('imports.url.submit')}
        </Button>
      </form>
    </PageContainer>
  );
}
